import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { addMonths, addYears } from "date-fns";

const CreateNewGiftCard: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [validity, setValidity] = useState("");
  const [redemption, setRedemption] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [malls, setMalls] = useState<
    Array<{ id: number; name: string; currency?: string | null }>
  >([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [selectedMallCurrency, setSelectedMallCurrency] = useState<string>("");
  const [isMallLoading, setIsMallLoading] = useState(false);

  const handleAmountChange = (value: string) => {
    const sanitized = value.replace(/\D/g, "");
    setAmount(sanitized);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Supported image formats
    const supportedFormats = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/tiff",
    ];

    if (!supportedFormats.includes(file.type)) {
      toast.error(
        "Please select a supported image format (JPEG, PNG, GIF, BMP, or TIFF)"
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setImageFile(file);
    setHasSubmitted(false); // Reset error state when image is uploaded
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const fetchMallOptions = useCallback(async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallsData.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
        currency: mall.currency ?? null,
      }));
      setMalls(mapped);
      setSelectedMallId(null);
      setSelectedMallCurrency("");
      setAmount("");
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      setSelectedMallId(null);
      setSelectedMallCurrency("");
      setAmount("");
      toast.error("Failed to load mall list");
    } finally {
      setIsMallLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMallOptions();
  }, [fetchMallOptions]);

  // Calculate validity date based on selection
  const calculateValidityDate = () => {
    const now = new Date();
    switch (validity) {
      case "1month":
        return addMonths(now, 1);
      case "3months":
        return addMonths(now, 3);
      case "6months":
        return addMonths(now, 6);
      case "9months":
        return addMonths(now, 9);
      case "1year":
        return addYears(now, 1);
      default:
        return null;
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!title.trim()) return toast.error("Title is required");
    if (!description.trim()) return toast.error("Description is required");
    if (!amount || parseFloat(amount) <= 0)
      return toast.error("Enter valid amount");
    if (!validity) return toast.error("Please select validity period");
    if (!redemption) return toast.error("Please select redemption type");
    if (!imageFile) return toast.error("Please upload an image");
    if (!selectedMallId) return toast.error("Please select a mall");

    setIsSubmitting(true);
    try {
      // Get userId and username from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userId = parsedUserInfo?.userId || 101;
      const userName = parsedUserInfo?.username || "";

      // Calculate validity date
      const validityDate = calculateValidityDate();

      // Create FormData
      const formData = new FormData();

      // Add image file (empty if no image selected)
      if (imageFile) {
        formData.append("File", imageFile);
      } else {
        formData.append("File", "");
      }

      // Create CommandJson object
      const commandJson = {
        p_Title: title,
        p_Description: description,
        p_Amount: parseFloat(amount),
        p_BannerImage: "",
        p_IsActive: true,
        p_CreatedBy: userId,
        p_Expiry: validityDate ? validityDate.toISOString().split(".")[0] : null,
        p_IsPartial: redemption === "Partial",
        p_MallId: selectedMallId,
        p_UserName: userName,
      };

      // Add CommandJson as string
      formData.append("CommandJosn", JSON.stringify(commandJson));

      // Call API
      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/CreateGiftCard`,
        formData,
        {
          headers: {
            xapikey: config.GIFT_API_KEY,
          },
        }
      );

      console.log("Gift card created:", response.data);

      // Check for error_msg in response
      const errorMsg = response.data?.error_msg;
      if (errorMsg && errorMsg.trim() !== "") {
        // Show error message and stay on the same page
        toast.error(errorMsg);
        return;
      }

      // No error, proceed with success flow
      toast.success("Gift card created successfully!");
      navigate("/giftcard/all-giftcards");
    } catch (error) {
      console.error("Error creating gift card:", error);
      toast.error("Failed to create gift card");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/giftcard/all-giftcards")}
          className="p-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Create New Gift Card
          </h1>
          <p className="text-gray-600 mt-1">
            Upload image, set details, and create a gift card
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Gift Card Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            {/* Image Upload */}
            <div className="space-y-2">
              <Label htmlFor="image">Image *</Label>
              <div>
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      className="w-48 h-32 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={removeImage}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label
                    htmlFor="image"
                    className={`w-48 h-32 flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      hasSubmitted && !imageFile
                        ? "border-red-300 hover:border-red-400 bg-red-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <Upload
                      className={`h-8 w-8 ${
                        hasSubmitted && !imageFile
                          ? "text-red-400"
                          : "text-gray-400"
                      }`}
                    />
                    <div
                      className={`text-xs mt-2 px-2 text-center font-medium ${
                        hasSubmitted && !imageFile
                          ? "text-red-600"
                          : "text-gray-600"
                      }`}
                    >
                      Click to upload image *
                    </div>
                    {hasSubmitted && !imageFile && (
                      <div className="text-xs text-red-500 mt-1 px-2 text-center">
                        Required field
                      </div>
                    )}
                  </label>
                )}
              </div>
              <Input
                id="image"
                type="file"
                accept=".jpeg,.jpg,.png,.gif,.bmp,.tiff,image/jpeg,image/png,image/gif,image/bmp,image/tiff"
                onChange={onImageChange}
                className="hidden"
              />
            </div>

            {/* Supported formats info */}
            <p className="text-sm text-gray-500">
              Supported formats: JPEG (.jpeg, .jpg), PNG (.png), GIF (.gif), BMP
              (.bmp), TIFF (.tiff)
            </p>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter gift card title"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Enter description"
              />
            </div>

            {/* Mall */}
            <div className="space-y-2">
              <Label htmlFor="mall">Mall *</Label>
              <Select
                value={
                  selectedMallId !== null
                    ? selectedMallId.toString()
                    : undefined
                }
                onValueChange={(value) => {
                  const nextId = Number(value);
                  setSelectedMallId(nextId);
                  const selected = malls.find((m) => m.id === nextId);
                  setSelectedMallCurrency(selected?.currency ?? "");
                  setAmount("");
                }}
                disabled={isMallLoading || malls.length === 0}
              >
                <SelectTrigger id="mall">
                  <SelectValue
                    placeholder={
                      isMallLoading ? "Loading malls..." : "Select Mall"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="!max-h-48 overflow-auto py-0">
                  {malls.length > 0 ? (
                    malls.map((mall) => (
                      <SelectItem key={mall.id} value={mall.id.toString()}>
                        {mall.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {isMallLoading ? "Loading..." : "No malls found"}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount *
                {selectedMallCurrency ? (
                  <span className="text-xs font-normal text-gray-500">
                    {" "}
                    (in {selectedMallCurrency})
                  </span>
                ) : null}
              </Label>
              <Input
                id="amount"
                inputMode="numeric"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder={
                  selectedMallId
                    ? "Enter amount"
                    : "Select mall to enter amount"
                }
                disabled={!selectedMallId}
              />
            </div>

            {/* Redemption */}
            <div className="space-y-2">
              <Label htmlFor="redemption">Redemption *</Label>
              <Select value={redemption} onValueChange={setRedemption}>
                <SelectTrigger>
                  <SelectValue placeholder="Select redemption type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Partial">Partial</SelectItem>
                  <SelectItem value="Full">Full</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Validity */}
            <div className="space-y-2">
              <Label htmlFor="validity">Validity *</Label>
              <Select value={validity} onValueChange={setValidity}>
                <SelectTrigger id="validity">
                  <SelectValue placeholder="Select validity period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1month">1 Month</SelectItem>
                  <SelectItem value="3months">3 Months</SelectItem>
                  <SelectItem value="6months">6 Months</SelectItem>
                  <SelectItem value="9months">9 Months</SelectItem>
                  <SelectItem value="1year">1 Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-center pt-2">
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white px-8"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create Gift Card"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateNewGiftCard;
